/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-10-24 14:55:06                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-05 14:54:15                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Views
  * Layouts
  * Guards
  * router: Browser router
*/

/* Imports */
import { Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { env } from "../env";
import { api } from "../services/api/api";
import Loading from "../components/loading/Loading";
import lazyWithReload from "./lazyWithReload";

// diag_secu admin module: lazy-loaded so its heavy deps (@xyflow, elkjs, react-query) stay out of
// the main chunk. Mounted under /admin/diag-secu/* inside the existing super-admin section.
const DiagSecuApp = lazyWithReload(() =>
  import("@easytech/diag-secu-admin").then((m) => ({ default: m.DiagSecuApp }))
);

// Statistics module: lazy-loaded so Apache ECharts stays out of the main chunk — only managers open
// it, and only occasionally. The chunk measures ~213 kB gzip, of which ECharts is roughly 90 kB: the
// module was 123 kB when it rendered with recharts. Registration is already narrowed to three chart
// types and the SVG renderer alone (see charts/echarts.ts), so that 90 kB is the engine's floor
// rather than slack — which is precisely why this route must never be imported eagerly.
const Statistics = lazyWithReload(() => import("../views/statistics/Statistics"));
/***/

/* Views */
import { Dashboard } from "../views/dashboard/Dashboard";
import { CreateWorkorder } from "../views/create-workorder/CreateWorkorder";
import { Planning } from "../views/planning/Planning";
import { Workorders } from "../views/workorders/Workorders";
import { Quotations } from "../views/quotations/Quotations";
import { Quotation } from "../views/quotation/Quotation";
import { Replenishments } from "../views/replenishments/Replenishments";
import { Replenishment } from "../views/replenishment/Replenishment";
import { Prospect } from "../views/prospect/Prospect";
import { Prospects } from "../views/prospects/Prospects";
import { Customers } from "../views/customers/Customers";
import { Customer } from "../views/customer/Customer";
import { Machine } from "../views/machine/Machine";
import { Users } from "../views/users/Users";
import { User } from "../views/user/User";
import { Parts } from "../views/parts/Parts";
import { Part } from "../views/part/Part";
import { Services } from "../views/services/Services";
import { Service } from "../views/service/Service";
import NotFound from "../views/not-found/NotFound";
import SuperAdmin from "../views/super-admin/SuperAdmin";
import AdminInterventions from "../views/super-admin/views/interventions/AdminInterventions";
import AdminProspects from "../views/super-admin/views/prospects/AdminProspects";
import AdminUsers from "../views/super-admin/views/users/AdminUsers";
import AdminCustomers from "../views/super-admin/views/customers/AdminCustomers";
/***/

/* Layouts */
import NavigationLayout from "../components/navigation-layout/NavigationLayout";
import DrawerLayout from "../components/drawer-layout/DrawerLayout";
/***/

/* Guards */
import ConnectedGuard from "../services/auth/guards/ConnectedGuard";
import { Workorder } from "../views/workorder/Workorder";
import SuperAdminGuard from "../services/auth/guards/SuperAdminGuard";
import RoleGuard from "../services/auth/guards/RoleGuard";
import { MANAGER_ROLES } from "../hooks/useUserRoles";
import AdminCompinfos from "../views/super-admin/views/compinfos/AdminCompinfos";
import AdminSettings from "../views/super-admin/views/settings/AdminSettings";
import AdminTools from "../views/super-admin/views/tools/AdminTools";
import AdminAutoDiag from "../views/super-admin/views/auto-diag/AdminAutoDiag";
import AdminOdoo from "../views/super-admin/views/odoo/AdminOdoo";
/***/

/**
* router: Browser router
* Contain all path and discribe the views that will be 
* display on this path
*/
export const router = createBrowserRouter([{
  path: "/",
  element:
    <DrawerLayout>
      <ConnectedGuard>
        <NavigationLayout>
          <Outlet />
        </NavigationLayout>
      </ConnectedGuard>
    </DrawerLayout>,
  children: [
    { path: "/dashboard", element: <Dashboard />, },
    { path: "/create-workorder", element: <CreateWorkorder />, },
    { path: "/planning", element: <Planning />, },
    { path: "/workorders", element: <Workorders />, },
    { path: "/workorders/:id", element: <Workorder /> },
    { path: "/quotations", element: <Quotations />, },
    { path: "/quotations/:id", element: <Quotation />, },
    { path: "/replenishments", element: <Replenishments />, },
    { path: "/replenishments/:id", element: <Replenishment />, },
    { path: "/parts", element: <Parts />, },
    { path: "/parts/:id", element: <Part />, },
    { path: "/services", element: <Services />, },
    { path: "/services/:id", element: <Service />, },
    { path: "/visits", element: <Prospects />, },
    { path: "/visits/:id", element: <Prospect />, },
    { path: "/customers", element: <Customers />, },
    { path: "/customers/:id", element: <Customer />, },
    { path: "/customers/:customerId/machines/:id", element: <Machine />, },
    { path: "/users", element: <Users />, },
    { path: "/users/:id", element: <User />, },
    {
      path: "/statistics",
      element:
        <RoleGuard roles={MANAGER_ROLES}>
          <Suspense fallback={<Loading />}>
            <Statistics />
          </Suspense>
        </RoleGuard>
    }
  ]
}, {
  path: "*",
  element: <NotFound />
}, {
  path: "/admin",
  element:
    <DrawerLayout>
      <SuperAdminGuard>
        <SuperAdmin />
      </SuperAdminGuard>
    </DrawerLayout>,
  children: [
    { path: "interventions", element: <AdminInterventions /> },
    { path: "interventions/:id", element: <Workorder /> },
    { path: "prospects", element: <AdminProspects /> },
    { path: "prospects/:id", element: <Prospect /> },
    { path: "users", element: <AdminUsers /> },
    { path: "customers", element: <AdminCustomers /> },
    { path: "users/:id", element: <User /> },
    { path: "compinfos", element: <AdminCompinfos /> },
    { path: "settings", element: <AdminSettings /> },
    { path: "odoo", element: <AdminOdoo /> },
    { path: "tools", element: <AdminTools /> },
    { path: "auto-diag", element: <AdminAutoDiag /> },
    {
      path: "diag-secu/*",
      element:
        <Suspense fallback={<Loading />}>
          <DiagSecuApp
            apiBase={env.DIAG_SECU_API_BASE}
            onCorePrefetch={() =>
              api.post("admin/safety-diag/prefetch", {}) as Promise<{ versionNumber?: number }>
            }
            fetchLanguages={async () => {
              const res = (await api.get("admin/languages")) as any[];
              return (res || []).map((l: any) => ({
                isoCode: l.isoCode,
                label: l.label,
                isDefault: Boolean(l.isDefault ?? l.default),
                enabled: l.enabled !== false,
              }));
            }}
          />
        </Suspense>
    },
    { path: "", element: <Navigate to="interventions" /> }
  ]
}]);
/***/
